import { Response } from "@/types/response/Response";
import ApiUtils from "../../lib/ApiUtils";
import { createApi } from "@reduxjs/toolkit/query/react";
import { ProviderCreateDTO, ProviderDTO, ProviderUpdateDTO } from "@/types/provider/ProviderDTO";
import { ProviderDetailDTO } from "@/types/provider/ProviderDetailDTO";
import { ProviderAuthorizationDTO, ProviderAuthorizationTypeDTO } from "@/types/provider/ProviderAuthorizationDTO";

export const providerApiV1 = createApi({
  reducerPath: "_provider",
  baseQuery: ApiUtils.apiQuery("/v1"),
  tagTypes: [
    "_providers",
    "_single_provider",
    "_provider_authorization_types",
    "_provider_authorization",
  ],
  endpoints: (builder) => ({
    getProviders: builder.query<Response<ProviderDTO[]>, Partial<void>>({
      query: () => "providers",
      providesTags: ["_providers"],
    }),
    getProviderById: builder.query<
      Response<ProviderDetailDTO>,
      Partial<string | undefined>
    >({
      query: (id) => "providers/" + id,
      providesTags: ["_single_provider"],
    }),
    updateProvider: builder.mutation<void, Partial<ProviderUpdateDTO>>({
      query: (body) => ({
        url: "providers/" + body.id,
        method: "PATCH",
        body: { ...body, id: undefined },
      }),
      invalidatesTags: [
        "_providers",
        "_single_provider",
        "_provider_authorization",
      ],
    }),
    createProvider: builder.mutation<
      Response<number>,
      Partial<ProviderCreateDTO>
    >({
      query: (body) => ({
        url: "providers",
        method: "POST",
        body,
      }),
      invalidatesTags: ["_providers"],
    }),
    updateProviderActiveState: builder.mutation<void, { id: number | string; active: boolean }>({
      query: ({ id, active }) => ({
        url: `providers/${id}/active-state?active=${active}`,
        method: "PUT",
      }),
      invalidatesTags: ["_providers", "_single_provider"],
    }),
    deleteProvider: builder.mutation<void, Partial<number | string>>({
      query: (id) => ({
        url: "providers/" + id,
        method: "DELETE",
      }),
      invalidatesTags: ["_providers"],
    }),
    getProviderAuthorizationTypes: builder.query<
      Response<ProviderAuthorizationTypeDTO[]>,
      Partial<void>
    >({
      query: (id) => "providers/authorization-types",
      providesTags: ["_provider_authorization_types"],
    }),
    getProviderAuthorizationById: builder.query<
      Response<ProviderAuthorizationDTO>,
      Partial<string | undefined>
    >({
      query: (id) => `providers/${id}/authorization`,
      providesTags: ["_provider_authorization_types"],
    }),
  }),
});

export const {
  useGetProvidersQuery,
  useGetProviderByIdQuery,
  useUpdateProviderMutation,
  useUpdateProviderActiveStateMutation,
  useCreateProviderMutation,
  useDeleteProviderMutation,
  useGetProviderAuthorizationTypesQuery,
  useGetProviderAuthorizationByIdQuery,
} = providerApiV1;
